import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Beem — Tactile button.
 * Signature treatment:
 *  - Pill by default (rounded-full).
 *  - Inner top highlight + soft rose glow for primary → feels 3D.
 *  - Lift on hover (-translate-y) + press (scale-98) on active.
 *  - Ghost/outline/link keep quieter treatment but still animate.
 */
const buttonVariants = cva(
  "relative inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium text-sm select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transition-[transform,box-shadow,background-color,color] duration-150 ease-out",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground rounded-full btn-tactile hover:bg-primary/95",
        destructive:
          "bg-destructive text-destructive-foreground rounded-full btn-tactile hover:bg-destructive/95",
        outline:
          "border border-border bg-background/60 backdrop-blur-sm rounded-full text-foreground hover:bg-accent hover:text-accent-foreground hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.98]",
        secondary:
          "bg-accent text-accent-foreground rounded-full hover:bg-accent/80 hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.98]",
        ghost:
          "rounded-full text-foreground/80 hover:bg-accent hover:text-accent-foreground active:scale-[0.97]",
        link:
          "text-primary underline-offset-[6px] hover:underline decoration-primary/50",
        soft:
          "bg-accent text-primary rounded-full hover:bg-accent/70 hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.98]",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 px-4 text-xs",
        lg: "h-12 px-8 text-[15px]",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
